import { ISize } from '@modules/order/stores/view/List/ListUIStore'
import formatNumberByLocale from '@utils/format-number-by-locale'
import classNames from 'classnames'
import map from 'lodash/map'
import React, {
  ChangeEvent,
  createRef,
  forwardRef,
  InputHTMLAttributes,
  MouseEvent,
  FocusEvent,
  useImperativeHandle,
  useMemo,
  useRef,
  useCallback
} from 'react'
import { useTranslation } from 'react-i18next'
import QtyCellLabelText from '../../Quantities/components/QtyCellLabelText'
import QtyCellValueText from '../../Quantities/components/QtyCellValueText'
import BookAndActualQty from './components/BookAndActualQty'
import SizedQuantity from './components/SizedQuantity'
import * as css from './QuantityCell.css'
import { config } from '@config'
import { stores } from '@stores'

interface QuantityCellProps {
  label?: string
  showBookCheckbox?: boolean
  onIsBookedChanged?: InputHTMLAttributes<HTMLInputElement>['onChange']
  disabled: boolean
  doorProduct: any
  onTotalByDraftQuantityChange: InputHTMLAttributes<HTMLInputElement>['onChange']
  onTotalByDraftQuantityKeyDown: InputHTMLAttributes<HTMLInputElement>['onKeyDown']
  onTotalByDraftQuantityFocus: InputHTMLAttributes<HTMLInputElement>['onFocus']
  onTotalByDraftQuantityMouseDown: InputHTMLAttributes<HTMLInputElement>['onMouseDown']
  autofocus?: boolean
  showSizeQuantitySummary: boolean
  sizes: ISize[]
  isUnsized?: boolean
  onSizeQuantityChange: (sizeId: string, e: ChangeEvent<HTMLInputElement>) => void
  onSizeQuantityFocus: (sizeId: string, e: FocusEvent<HTMLInputElement>) => void
  onSizeQuantityMouseDown: (sizeId: string, e: MouseEvent) => void
  onSizeQuantityKeyDown: InputHTMLAttributes<HTMLInputElement>['onKeyDown']
  navigateToCell: (next: boolean) => void
  navigateToSize: (index: number) => void
  onBlurHandler?: () => void
  highlightDoors?: boolean
}

export interface QuantityCellRef {
  quantityInputRef: HTMLInputElement
  focusQtyInput: () => void
  focusSizeInput: (index) => void
}

const QuantityCell = forwardRef<QuantityCellRef, QuantityCellProps>(
  (
    {
      label,
      showBookCheckbox = true,
      onTotalByDraftQuantityChange,
      disabled,
      doorProduct,
      onIsBookedChanged,
      onTotalByDraftQuantityKeyDown,
      onTotalByDraftQuantityFocus,
      onTotalByDraftQuantityMouseDown,
      autofocus,
      showSizeQuantitySummary,
      sizes,
      onSizeQuantityChange,
      onSizeQuantityKeyDown,
      onSizeQuantityMouseDown,
      onSizeQuantityFocus,
      navigateToCell,
      navigateToSize,
      isUnsized,
      onBlurHandler,
      highlightDoors
    },
    ref
  ) => {
    const totalByDraftQuantityInputRef = useRef<HTMLInputElement>()
    const { t } = useTranslation()
    const {
      productConfiguration: { enhancedViewEnabled, featureFlags }
    } = stores

    const keyDownHandler = useCallback(
      (e, currSizeIndex) => {
        if (e.which === 9) {
          if (e.shiftKey) {
            navigateToSize(currSizeIndex - 1)
          } else {
            navigateToSize(currSizeIndex + 1)
          }
          e.preventDefault()
        }
        onSizeQuantityKeyDown?.(e)
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    )

    const lastSizeKeyDownHandler = useCallback(
      (e, availableSizes) => {
        if (e.which === 9) {
          if (e.shiftKey) {
            navigateToSize(availableSizes.length - 1)
            ;(sizesEle[availableSizes.length - 1] as any)?.ref.current?.focus()
          } else {
            navigateToCell(true)
          }
          e.preventDefault()
        }
        onSizeQuantityKeyDown(e)
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    )

    const sizesEle = useMemo(
      () => {
        if (sizes.length) {
          const availableSizes = [...sizes]
          const availableSizesRef = availableSizes.map(() =>
            createRef<HTMLInputElement>()
          )
          const lastSize = availableSizes.pop()
          const lastSizeRef = availableSizesRef.pop()
          return availableSizes
            ?.map(({ id: sizeId, isInactive = false, ...rest }, currSizeIndex) => {
              return (
                <SizedQuantity
                  key={sizeId}
                  ref={availableSizesRef[currSizeIndex]}
                  onChange={e => onSizeQuantityChange?.(sizeId, e)}
                  value={doorProduct?.sizeQuantities?.[sizeId]?.quantity || 0}
                  disabled={!doorProduct || disabled}
                  className={classNames({ [css['inactive-size']]: isInactive })}
                  onKeyDown={e => keyDownHandler(e, currSizeIndex)}
                  onFocus={e => {
                    navigateToSize(currSizeIndex)
                    onSizeQuantityFocus?.(sizeId, e)
                  }}
                  onMouseDown={e => {
                    navigateToSize(currSizeIndex)
                    onSizeQuantityMouseDown?.(sizeId, e)
                  }}
                  onBlur={onBlurHandler}
                />
              )
            })
            .concat([
              <SizedQuantity
                key={lastSize.id}
                ref={lastSizeRef}
                onChange={e => onSizeQuantityChange?.(lastSize.id, e)}
                value={doorProduct?.sizeQuantities?.[lastSize.id]?.quantity || 0}
                disabled={!doorProduct || disabled}
                className={classNames({ [css['inactive-size']]: lastSize.isInactive })}
                onKeyDown={e => lastSizeKeyDownHandler(e, availableSizes)}
                onFocus={e => {
                  navigateToSize(availableSizes.length)
                  onSizeQuantityFocus?.(lastSize.id, e)
                }}
                onMouseDown={e => {
                  navigateToSize(availableSizes.length)
                  onSizeQuantityMouseDown?.(lastSize.id, e)
                }}
                onBlur={onBlurHandler}
              />
            ])
        }
        return []
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [sizes, disabled, doorProduct]
    )
    useImperativeHandle(
      ref,
      () => ({
        quantityInputRef: totalByDraftQuantityInputRef.current,
        sizeElementRefs: map(sizesEle, 'ref.current'),
        focusQtyInput: () => {
          totalByDraftQuantityInputRef?.current?.focus()
        },
        focusSizeInput: index => {
          ;(sizesEle[index] as any)?.ref.current?.focus()
        }
      }),
      [sizesEle]
    )

    const doorQtyKeyDownHandler = useCallback(
      e => {
        if (e.which === 9) {
          if (e.shiftKey) {
            navigateToCell(false)
          } else {
            if (sizesEle?.length) {
              navigateToSize(0)
            } else {
              navigateToCell(true)
            }
          }
          e.preventDefault()
        }
        onTotalByDraftQuantityKeyDown?.(e)
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [sizesEle]
    )

    const highlightQty = useMemo(() => {
      const highlightInput =
        doorProduct?.totalBySizeQuantity !== doorProduct?.totalByDraftQuantity
      return enhancedViewEnabled
        ? featureFlags?.highlightQuantityDiscrepancies && highlightInput
        : config.highlightQtyDiscrepanciesInSHR === 'true' && highlightInput
    }, [doorProduct, enhancedViewEnabled, featureFlags])

    return (
      <div
        className={classNames(
          'flex column w-100',
          css.doorQuantities,
          highlightDoors ? css.highlightQtyCell : null
        )}
      >
        <div>
          <BookAndActualQty
            label={label}
            showCheckbox={showBookCheckbox}
            value={doorProduct?.totalByDraftQuantity || 0}
            handleInputChange={onTotalByDraftQuantityChange}
            isBooked={Boolean(doorProduct)}
            highlightDoors={highlightDoors}
            disabled={disabled}
            onChange={onIsBookedChanged}
            onKeyDown={e => doorQtyKeyDownHandler(e)}
            ref={totalByDraftQuantityInputRef}
            autoFocus={autofocus}
            onFocus={onTotalByDraftQuantityFocus}
            onMouseDown={onTotalByDraftQuantityMouseDown}
            isUnsized={isUnsized}
            onBlur={onBlurHandler}
            highlightQty={highlightQty}
          />
        </div>
        <div>
          {!isUnsized && showSizeQuantitySummary && (
            <div className={classNames('flex column align-center justify-center')}>
              <div
                className={classNames(
                  'flex align-center column text-center',
                  css['size-quantity-summary']
                )}
              >
                <QtyCellLabelText
                  translation={t('orderTable.sizedQty', 'SIZED QUANTITY')}
                />
              </div>
              <div className={classNames('flex align-center column text-center')}>
                <QtyCellValueText
                  value={formatNumberByLocale(
                    sizes?.length
                      ? doorProduct?.totalBySizeQuantity || 0
                      : doorProduct?.totalByDraftQuantity || 0
                  )}
                  disabled={!doorProduct || disabled}
                />
              </div>
            </div>
          )}
          <div className={css.sizedQtyInput}>{sizesEle}</div>
        </div>
      </div>
    )
  }
)

export default QuantityCell
